/* ============================================================================
   haiku-mark-distort — pointer-driven scatter on the footer wordmark

   THE TECHNIQUE, and where it comes from
   akella/webgl-mouseover-effects, step 3, the "colorful" option. Read out of
   js/post/fragment.glsl in that repository (uType == 0), not from memory:

       float c = circle(newUV, uMouse, 0.0, 0.2);
       float r = texture2D(tDiffuse, newUV.xy += c * (uVelo * .5)).x;
       float g = texture2D(tDiffuse, newUV.xy += c * (uVelo * .525)).y;
       float b = texture2D(tDiffuse, newUV.xy += c * (uVelo * .55)).z;

   `circle` is a soft disc around the pointer, aspect-corrected by resolution.
   Note the `+=`: the UV walks FORWARD once per channel, each step slightly
   longer, so the three samples accumulate rather than fan out. Everything is
   multiplied by pointer speed, so the mark is untouched at rest.

   This is the gentler of the two options by construction — the offsets are
   fractions of the speed rather than ten times it, and the disc is twice as
   soft. The "random" scatter (uType == 2) was tried here first and read as too
   intense on a wordmark this size.

   STATELESS, and that is a choice with a cost. The previous build here kept a
   ping-pong velocity buffer, so a disturbance trailed and settled like a
   fluid. This is a pure function of where the pointer is and how fast it is
   going — the same as the reference — so there is no memory and no settle.
   That is the owner's explicit call.

   ONE DEPARTURE, forced by the subject. The reference distorts an opaque
   photograph and can add its glow straight onto colour. This is ink on
   transparency: the disturbance has to be carried in ALPHA or scattered glyph
   edges simply disappear. So alpha is sampled at the warped UV and the glow
   rides on it.

   THE BLEED. The canvas is deliberately LARGER than the wordmark — see PAD.
   Sized to the mark exactly, as it once was, every sample pushed outward
   landed past [0,1], hit CLAMP_TO_EDGE and sheared the outer letters. The mark
   is rasterised inset into a bigger bitmap and the canvas is pulled back out
   over it, so the ink lands where the SVG sat and the scatter has room.

   ADDITIVE AND FAIL-SAFE
   The canvas is drawn FIRST, read back with readPixels, and only if real ink
   comes back does .haiku-mark-fx-ready land — and every rule that hides the
   SVG is gated behind that class. A dead canvas here is invisible, never
   destructive.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.haikuMarkDistortConfig || {};
  if (cfg.enabled === false) return;

  /* ⚠️ Both of these silently disable the whole effect, and both have been
     mistaken for "it is broken" before — a Mac with Reduce Motion on, or an
     iPhone in Low Power Mode, gets no distortion at all and no clue why.
     `debug: true` in the snippet reports which guard fired, to the console. */
  var DEBUG = cfg.debug === true;

  /* ALWAYS published, debug or not. Read `haikuMarkFx` in the console and it
     says in one object why the canvas is or is not carrying the mark. This
     exists because the failure is invisible by design — every rule that hides
     the SVG is gated on proof, so a canvas that never drew looks exactly like
     a site with no effect, and three rounds went into guessing which. */
  var STATE = window.haikuMarkFx = {
    reachedInit: false, gl: false, program: false,
    textureBuilt: false, proven: false, reason: null,
    bufferW: 0, bufferH: 0, dpr: null, maxTexture: null
  };
  function bail(why) {
    STATE.reason = why;
    if (DEBUG && window.console) console.log('[haiku-mark-distort] off: ' + why);
  }

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return bail('prefers-reduced-motion is set (OS "reduce motion")');
  }
  if (window.matchMedia && window.matchMedia('(hover: none)').matches) {
    return bail('coarse pointer — no hover to drive it');
  }

  /* --- the reference's numbers, named ------------------------------------
     RADIUS  softness of the disc around the pointer
     AMOUNT  how far a fragment may scatter
     GLOW    how much the disturbed area lifts
     STRENGTH / VELO_SCALE / VELO_MAX
             map per-frame pointer travel onto the 0..0.05 speed band the
             reference clamps to
     FOLLOW / SMOOTH
             the reference's followMouse and targetSpeed. Per-60fps-frame
             rates, rescaled by real dt, so 60Hz and 120Hz match.
     PAD     bleed around the mark, as a fraction of its size            */
  var RADIUS     = typeof cfg.radius     === 'number' ? cfg.radius     : 0.20;
  var SPREAD     = typeof cfg.spread     === 'number' ? cfg.spread     : 0.5;
  var FRINGE     = typeof cfg.fringe     === 'number' ? cfg.fringe     : 1.0;
  var STRENGTH   = typeof cfg.strength   === 'number' ? cfg.strength   : 1.0;
  var VELO_SCALE = typeof cfg.veloScale  === 'number' ? cfg.veloScale  : 1.0;
  var VELO_MAX   = typeof cfg.veloMax    === 'number' ? cfg.veloMax    : 0.05;
  var FOLLOW     = typeof cfg.follow     === 'number' ? cfg.follow     : 0.22;
  var SMOOTH     = typeof cfg.smoothing  === 'number' ? cfg.smoothing  : 0.32;
  var PAD        = typeof cfg.pad        === 'number' ? cfg.pad        : 0.18;

  var VERT = '#version 300 es\n' +
    'in vec2 aPos;out vec2 vUv;void main(){vUv=aPos*0.5+0.5;gl_Position=vec4(aPos,0.0,1.0);}';

  var DRAW_FRAG = '#version 300 es\n' + [
    'precision highp float;',
    'in vec2 vUv; out vec4 outColor;',
    'uniform sampler2D uTexture;',
    'uniform vec2 uMouse, uResolution;',
    'uniform float uVelo, uFade, uRadius, uSpread, uFringe;',
    'float circle(vec2 uv, vec2 c, float r, float border){',
    '  uv -= c;',
    '  uv *= uResolution;',
    '  float d = sqrt(dot(uv, uv));',
    '  return smoothstep(r + border, r - border, d);',
    '}',
    'void main(){',
    '  vec2 uv = vec2(vUv.x, 1.0 - vUv.y);',
    '  float c = circle(vUv, uMouse, 0.0, uRadius);',
    // The reference walks the UV forward three times, once per channel, each
    // step slightly longer than the last — `newUV.xy += ...` inside each
    // texture call, so the offsets accumulate rather than fan out from centre.
    '  vec2 o1 = uv + c * (uVelo * uSpread);',
    '  vec2 o2 = o1 + c * (uVelo * uSpread * 1.05);',
    '  vec2 o3 = o2 + c * (uVelo * uSpread * 1.10);',
    '  float a1 = texture(uTexture, o1).a;',
    '  float a2 = texture(uTexture, o2).a;',
    '  float a3 = texture(uTexture, o3).a;',
    '  float a = max(max(a1, a2), a3);',
    '  vec3 ink = texture(uTexture, o2).rgb;',
    // THE ADAPTATION. The reference splits R, G and B of an opaque photograph.
    // This mark is one flat ink on transparency, so splitting its colour gives
    // three identical greys and no colour at all. The separation has to come
    // from COVERAGE instead: where a channel's own sample missed the glyph but
    // another caught it, that channel is lifted toward the page, and the
    // shortfall between the three is what shows as a coloured fringe along the
    // edges. Same idea, carried by alpha because alpha is all this mark has.
    '  vec3 rgb = clamp(ink + vec3(a - a1, a - a2, a - a3) * uFringe, 0.0, 1.0);',
    '  outColor = vec4(rgb, a * uFade);',
    '}'
  ].join('\n');

  function sh(gl, type, src) {
    var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      if (DEBUG && window.console) console.log('[haiku-mark-distort] shader: ' + gl.getShaderInfoLog(s));
      gl.deleteShader(s); return null;
    }
    return s;
  }
  function prog(gl, vsrc, fsrc) {
    var v = sh(gl, gl.VERTEX_SHADER, vsrc), f = sh(gl, gl.FRAGMENT_SHADER, fsrc);
    if (!v || !f) return null;
    var p = gl.createProgram(); gl.attachShader(p, v); gl.attachShader(p, f); gl.linkProgram(p);
    return gl.getProgramParameter(p, gl.LINK_STATUS) ? p : null;
  }

  function init(link) {
    STATE.reachedInit = true;
    STATE.dpr = window.devicePixelRatio || 1;
    var svg = link.querySelector('.haiku-footer__mark');
    if (!svg) return bail('no .haiku-footer__mark inside the link');

    var canvas = document.createElement('canvas');
    canvas.className = 'haiku-mark-fx';
    canvas.setAttribute('aria-hidden', 'true');
    link.appendChild(canvas);

    var gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: false, antialias: false });
    if (!gl) return bail('no WebGL2 context');
    STATE.gl = true;
    STATE.maxTexture = gl.getParameter(gl.MAX_TEXTURE_SIZE);

    var pDraw = prog(gl, VERT, DRAW_FRAG);
    if (!pDraw) return bail('draw program failed to build');
    STATE.program = true;

    var vao = gl.createVertexArray(); gl.bindVertexArray(vao);
    var buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);

    var uD = {
      tex: gl.getUniformLocation(pDraw, 'uTexture'),
      mouse: gl.getUniformLocation(pDraw, 'uMouse'),
      res: gl.getUniformLocation(pDraw, 'uResolution'),
      velo: gl.getUniformLocation(pDraw, 'uVelo'),
      fade: gl.getUniformLocation(pDraw, 'uFade'),
      radius: gl.getUniformLocation(pDraw, 'uRadius'),
      spread: gl.getUniformLocation(pDraw, 'uSpread'),
      fringe: gl.getUniformLocation(pDraw, 'uFringe')
    };

    var ready = false, proven = false, raf = 0, W = 0, H = 0, fade = 0;
    var mouse = [0.5, 0.5], ptr = [0.5, 0.5], ptrSeen = false;
    var velo = 0, lastT = 0;

    /* ⚠️ LAYOUT SIZE, NOT THE VISUAL RECT.
       `.haiku-footer__mark-link` carries a transform — measured
       matrix(0.8, 0, 0, 0.8, 0, 0) at rest — and getBoundingClientRect()
       reports the box AFTER it. The canvas is a child, so the lengths written
       below are pre-transform and would be scaled by that 0.8 a second time.
       Sizing from the rect therefore shrinks the mark every build: caught
       rendering a 1060-wide raster inside a 1442 box painting at 1154.
       offsetWidth/offsetHeight on the <a> are layout lengths and ignore it. */
    function buildTexture(done) {
      var r = svg.getBoundingClientRect();
      var lw = link.offsetWidth || r.width, lh = link.offsetHeight || r.height;
      if (!lw || !lh) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.max(2, Math.round(lw * dpr)), h = Math.max(2, Math.round(lh * dpr));
      var ink = getComputedStyle(svg).color || '#000';
      var clone = svg.cloneNode(true);
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clone.setAttribute('width', w); clone.setAttribute('height', h);
      clone.setAttribute('fill', ink); clone.style.color = ink;

      var img = new Image();
      img.onload = function () {
        var padX = Math.round(w * PAD), padY = Math.round(h * PAD);
        var bw = w + padX * 2, bh = h + padY * 2;

        var off = document.createElement('canvas');
        off.width = bw; off.height = bh;
        var octx = off.getContext('2d');
        if (!octx) return;
        octx.drawImage(img, padX, padY, w, h);

        canvas.width = bw; canvas.height = bh; W = bw; H = bh;
        // CSS px, not device px — the buffer is dpr-scaled, the box is not.
        canvas.style.left = -(padX / dpr) + 'px';
        canvas.style.bottom = -(padY / dpr) + 'px';
        canvas.style.width = (bw / dpr) + 'px';
        canvas.style.height = (bh / dpr) + 'px';

        gl.bindTexture(gl.TEXTURE_2D, tex);
        try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, off); }
        catch (e) { return bail('texImage2D threw: ' + e); }
        ready = true;
        STATE.textureBuilt = true; STATE.bufferW = bw; STATE.bufferH = bh;
        /* ⚠️ A buffer wider than the driver's limit is silently an incomplete
           texture: it draws nothing and reports no error. At dpr 2 on a wide
           window this is genuinely reachable. */
        if (STATE.maxTexture && (bw > STATE.maxTexture || bh > STATE.maxTexture)) {
          bail('bitmap ' + bw + 'x' + bh + ' exceeds MAX_TEXTURE_SIZE ' + STATE.maxTexture);
        }
        if (done) done();
      };
      img.onerror = function () { bail('the serialised SVG failed to decode'); };
      img.src = 'data:image/svg+xml;charset=utf-8,' +
        encodeURIComponent(new XMLSerializer().serializeToString(clone));
    }

    function drawMark() {
      gl.viewport(0, 0, W, H);
      gl.useProgram(pDraw);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tex); gl.uniform1i(uD.tex, 0);
      gl.uniform2f(uD.mouse, mouse[0], mouse[1]);
      // aspect correction, as the reference's `resolution` does it
      gl.uniform2f(uD.res, 1.0, W ? H / W : 1.0);
      gl.uniform1f(uD.velo, velo);
      gl.uniform1f(uD.fade, fade);
      gl.uniform1f(uD.radius, RADIUS);
      gl.uniform1f(uD.spread, SPREAD);
      gl.uniform1f(uD.fringe, FRINGE);
      gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    /* The pointer handler RECORDS ONLY. Injecting from the event was the old
       stagger: measured 61 pointermove events against 17 animation frames, so
       most of the hand's motion was discarded and a pause re-fed a stale
       value. The frame decides how far the pointer travelled. */
    function onMove(e) {
      var r = link.getBoundingClientRect();
      ptr[0] = (e.clientX - r.left) / r.width;
      ptr[1] = 1.0 - (e.clientY - r.top) / r.height;
      if (!ptrSeen) { mouse[0] = ptr[0]; mouse[1] = ptr[1]; ptrSeen = true; }
      kick();
    }

    function loop(now) {
      var t = now || performance.now();
      var dt = lastT ? Math.min(64, t - lastT) : 16.7;
      lastT = t;
      var f = dt / 16.7;

      // followMouse
      var k = 1 - Math.pow(1 - FOLLOW, f);
      var nx = mouse[0] + (ptr[0] - mouse[0]) * k;
      var ny = mouse[1] + (ptr[1] - mouse[1]) * k;
      // targetSpeed — the distance actually covered this frame
      var step = Math.sqrt((nx - mouse[0]) * (nx - mouse[0]) + (ny - mouse[1]) * (ny - mouse[1]));
      mouse[0] = nx; mouse[1] = ny;

      var sm = 1 - Math.pow(1 - SMOOTH, f);
      velo += (Math.min(step * STRENGTH * VELO_SCALE / (dt / 16.7), VELO_MAX) - velo) * sm;

      drawMark();

      var chasing = Math.abs(ptr[0] - mouse[0]) + Math.abs(ptr[1] - mouse[1]);
      if (velo > 0.0002 || chasing > 0.0008) raf = requestAnimationFrame(loop);
      else { raf = 0; lastT = 0; velo = 0; drawMark(); }
    }
    function kick() { if (!raf) { lastT = 0; raf = requestAnimationFrame(loop); } }

    /* THE PROOF: draw once at full strength, read the middle row back, and
       require real alpha. Only then may the SVG be hidden. */
    function prove() {
      fade = 1; velo = VELO_MAX; mouse = [0.5, 0.5]; ptr = [0.5, 0.5];
      drawMark();
      /* ⚠️ READ FROM THE MIDDLE, NOT FROM x=0.

         This is where the bleed bit. The strip starts at the horizontal centre
         of the buffer because the first `PAD * width` columns are transparent
         padding by construction — reading from 0 samples the bleed and nothing
         else, concludes the canvas drew nothing, and leaves the SVG up.

         It only shows on a 2x display, which is why it survived every test
         here and failed on the owner's machine every time: at dpr 1 the pad is
         191px and a 512px window still reaches the first glyph; at dpr 2 it is
         596px, wider than the whole window. Reported as `proven: false,
         readPixels found no ink at row 736 of 1472`.

         Three rows rather than one, too — a single row through a counter or a
         gap between letters is a real way to miss ink that is genuinely
         there. */
      var n = Math.min(W, 512);
      var x0 = Math.max(0, Math.floor((W - n) / 2));
      var px = new Uint8Array(n * 4);
      var hit = 0;
      var rows = [Math.floor(H * 0.5), Math.floor(H * 0.38), Math.floor(H * 0.62)];
      for (var r = 0; r < rows.length && hit <= 4; r++) {
        gl.readPixels(x0, rows[r], n, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
        for (var i = 3; i < px.length; i += 4) if (px[i] > 8) { hit++; if (hit > 4) break; }
      }
      proven = hit > 4;
      STATE.proven = proven;
      velo = 0;
      if (proven) {
        fade = 1;
        link.style.setProperty('--hf-fx', '1');
        drawMark();
        link.classList.add('haiku-mark-fx-ready');
      } else {
        fade = 0; drawMark();
        bail('readPixels found no ink in ' + n + 'px from x=' + x0 +
             ' across 3 rows of ' + H + ' — canvas ' + W + 'x' + H +
             ', SVG left in place');
      }
    }

    /* The mark used to be blank until first hover: buildTexture returns early
       with no measurable size, and at init the footer is far below the fold.
       This retries once it actually has one. */
    function repaint() {
      var r = link.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return;
      if (!ready) { buildTexture(function () { prove(); }); return; }
      if (!proven) { prove(); return; }
      fade = 1; velo = 0; drawMark();
    }

    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) if (entries[i].isIntersecting) repaint();
      }, { rootMargin: '300px' }).observe(link);
    } else {
      window.addEventListener('scroll', repaint, { passive: true });
    }

    window.addEventListener('resize', repaint);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(repaint);

    buildTexture(function () {
      prove();
      if (!proven) return;
      link.addEventListener('pointermove', onMove);
    });

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () {
        var lw = link.offsetWidth || svg.getBoundingClientRect().width;
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var base = Math.round(lw * dpr);
        // W is the PADDED buffer width — compare like with like.
        if (Math.abs(base + Math.round(base * PAD) * 2 - W) > 2) buildTexture();
      }, 180);
    }, { passive: true });

    // the mark is redrawn in the other ink when the mode flips
    new MutationObserver(function () { buildTexture(); })
      .observe(document.documentElement, { attributes: true, attributeFilter: ['data-mode'] });
  }

  function boot() {
    var l = document.querySelectorAll('.haiku-footer__mark-link');
    if (!l.length) return bail('no .haiku-footer__mark-link on the page');
    for (var i = 0; i < l.length; i++) init(l[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
